import { DateTime } from 'luxon';

import {
  getLongTermContractAttributes,
  IAdminBookingDate,
  ILongContractAttributes,
} from '@pharmaplan/common';

import { toStandardFormat } from '../../helpers/Functions';
import { MomentTimeFormats } from '../../helpers/Constants';
import { useAppSelector } from '../useAppSelector';

const fallbackDate = new Date();

const parseBookingDate = (date: string) =>
  DateTime.fromISO(date, { zone: 'utc' });

const useGetChangedContract = () => {
  const longTermAttributes = useAppSelector(getLongTermContractAttributes);
  const {
    defaultEndTime,
    defaultStartTime,
    startDate,
    endDate,
    shiftContractAttributes,
  } = longTermAttributes ?? {};

  const comparator = () => {
    const { hour: sHour, minute: sMinute } = DateTime.fromJSDate(
      (defaultStartTime as Date) ?? fallbackDate,
    );

    const { hour: eHour, minute: eMinute } = DateTime.fromJSDate(
      (defaultEndTime as Date) ?? fallbackDate,
    );

    return {
      startDate: toStandardFormat(
        startDate?.set({
          hour: sHour,
          minute: sMinute,
          second: 0,
        }),
      ),
      endDate: toStandardFormat(
        endDate?.set({
          hour: eHour,
          minute: eMinute,
          second: 0,
        }),
      ),
    };
  };

  const getTimeOnly = (dateString: string) =>
    dateString?.split?.('T')?.[1] ?? '';

  const getChangedItems = () =>
    shiftContractAttributes?.filter(
      (item) =>
        !(
          getTimeOnly(item.startDate) === getTimeOnly(comparator().startDate)
          && getTimeOnly(item.endDate) === getTimeOnly(comparator().endDate)
        ) || item.dayOff,
    );

  const getChangedItemsFromParams = (
    attributes: Array<ILongContractAttributes>,
    compareStart: string,
    compareEnd: string,
  ) =>
    attributes?.filter(
      (item) =>
        !(
          getTimeOnly(item.startDate) === getTimeOnly(compareStart)
          && getTimeOnly(item.endDate) === getTimeOnly(compareEnd)
        ),
    );

  const getEditedBookingTimes = (bookings: IAdminBookingDate[]) =>
    bookings.flatMap((booking) => {
      const attribute = shiftContractAttributes.find((item) =>
        item.startDate.split('T')[0] === booking.startDate.split('T')[0]);

      if (!attribute) return [];

      const editedStart = parseBookingDate(attribute.startDate);
      const editedEnd = parseBookingDate(attribute.endDate);
      const hasChanged = editedStart.toMillis() !== parseBookingDate(booking.startDate).toMillis()
        || editedEnd.toMillis() !== parseBookingDate(booking.endDate).toMillis();

      return hasChanged ? [{
        startDate: editedStart.toFormat(MomentTimeFormats.fullDate),
        endDate: editedEnd.toFormat(MomentTimeFormats.fullDate),
      }] : [];
    });

  return { getChangedItems, comparator, getChangedItemsFromParams, getEditedBookingTimes };
};

export default useGetChangedContract;
